import { Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useAnswer } from "src/context/AnswerProvider";
import { useQuestions } from "src/context/QuestionsProvider";
import { useVisit } from "src/context/VisitProvider";
import { QuestionTypes } from "../../constants";
import { useSubmitAnswers } from "src/hooks/useSubmitAnswers";
import { useGetNextFromOptions } from "src/hooks/useGetNextFromOptions";

const QuestionFooter = () => {
  const visitCompleted = useVisit((v) => v.completed_at != null);
  const hasFollowUp = useQuestions((q) => q.follow_up);
  const requiredAnswer = useQuestions((q) => q.required_answer);
  const questionType = useQuestions((q) => q.question_type);
  const questionSlug = useQuestions((q) => q.slug);
  const questionTag = useQuestions((q) => q.tag);
  const { answers, details } = useAnswer();
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { submitAnswer } = useSubmitAnswers();
  const next = useGetNextFromOptions(answers);

  const nextQuestion = useCallback(() => {
    //hasFollowUp check is just for an extra measure to be safe
    if (hasFollowUp && answers.includes("Yes") && (details?.length || 0) < 2) {
      const errorMessage = t(`questions.followUpError`);

      setError(errorMessage);
      return;
    }

    setError(null);
    submitAnswer(answers, next ?? undefined);
  }, [hasFollowUp, answers, details?.length, submitAnswer, next, t]);

  const buttonText = useMemo(() => {
    if (questionSlug === "1CixX4jj") {
      return t("button.complete-cancellation", "Complete cancellation ");
    }

    return t("button.next", "Next");
  }, [questionSlug, t]);

  if (
    [
      QuestionTypes.PROGRESS_PROJECTION,
      QuestionTypes.HEIGHT_WEIGHT,
      QuestionTypes.BIRTHDAY,
      QuestionTypes.GLP1_PRELIMINARY_RESULTS,
      QuestionTypes.GLP1_REVIEWS,
      QuestionTypes.CANCELLATION_MESSAGE,
      QuestionTypes.CANCELLATION_REVIEW,
      QuestionTypes.ED_OPTIONS,
      QuestionTypes.SHIPPING_INFORMATION,
      QuestionTypes.ID_VERIFICATION,
      QuestionTypes.PAYMENT_CONFIRMATION,
      QuestionTypes.PAYMENT_INFORMATION,
      QuestionTypes.STATE_DISQUALIFICATION,
      QuestionTypes.PHARMACY_SEARCH,
    ].includes(questionType) ||
    ([QuestionTypes.SINGLE_CHOICE].includes(questionType) && !hasFollowUp) ||
    ["AMH_REVIEW"].includes(questionTag || "")
  )
    return null;

  return (
    <>
      {error ? (
        <Typography sx={{ color: "red" }} textAlign="center">
          {error}
        </Typography>
      ) : null}
      <LoadingButton
        loading={false}
        onClick={nextQuestion}
        disabled={
          answers.length == null ||
          (answers.length === 0 && requiredAnswer) ||
          visitCompleted
        }
      >
        {buttonText}
      </LoadingButton>
    </>
  );
};

export default QuestionFooter;
